import { useEffect, useRef } from 'react'
import { gsap } from 'gsap'
import { GRID } from './constants'
import { GRID_IMAGES } from './images'
import { FONT_STYLES } from '@/styles/fonts'

interface GridItem {
  src: string
  caption: string
  x: number
  y: number
  w: number
  h: number
}

interface GridSectionProps {
  sources?: Array<{ src: string; caption: string }>
  data?: Array<{ x: number; y: number; w: number; h: number }>
  originalSize?: { w: number; h: number }
}

const defaultSources: Array<{ src: string; caption: string }> = GRID_IMAGES

const defaultData: Array<{ x: number; y: number; w: number; h: number }> = [
  { x: 71, y: 58, w: 400, h: 270 },
  { x: 211, y: 255, w: 540, h: 360 },
  { x: 631, y: 158, w: 400, h: 270 },
  { x: 1191, y: 245, w: 260, h: 195 },
  { x: 351, y: 687, w: 260, h: 290 },
  { x: 751, y: 824, w: 205, h: 154 },
  { x: 911, y: 540, w: 260, h: 350 },
  { x: 1051, y: 803, w: 400, h: 300 },
]

export default function GridSection({
  sources = defaultSources,
  data = defaultData,
  originalSize = { w: 1522, h: 1238 },
}: GridSectionProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const itemsRef = useRef<Array<{
    el: HTMLDivElement
    wrapper: HTMLDivElement
    img: HTMLImageElement
    x: number
    y: number
    w: number
    h: number
    extraX: number
    extraY: number
    rect: DOMRect
    ease: number
  }>>([])
  const scrollRef = useRef({
    ease: GRID.SCROLL_EASE,
    current: { x: 0, y: 0 },
    target: { x: 0, y: 0 },
    last: { x: 0, y: 0 },
    delta: { x: { c: 0, t: 0 }, y: { c: 0, t: 0 } },
  })
  const mouseRef = useRef({
    x: { t: 0.5, c: 0.5 },
    y: { t: 0.5, c: 0.5 },
    press: { t: 0, c: 0 },
  })
  const isDraggingRef = useRef(false)
  const dragRef = useRef({ startX: 0, startY: 0, scrollX: 0, scrollY: 0 })
  const tileSizeRef = useRef({ w: 0, h: 0 })
  const winSizeRef = useRef({ w: 0, h: 0 })
  const animationFrameRef = useRef<number | null>(null)
  const introItemsRef = useRef<HTMLDivElement[]>([])
  const isAnimatingRef = useRef(false)

  useEffect(() => {
    if (!containerRef.current) return

    const container = containerRef.current

    const onResize = () => {
      winSizeRef.current.w = window.innerWidth
      winSizeRef.current.h = window.innerHeight

      tileSizeRef.current.w = winSizeRef.current.w
      tileSizeRef.current.h = winSizeRef.current.w * (originalSize.h / originalSize.w)

      scrollRef.current.current = { x: 0, y: 0 }
      scrollRef.current.target = { x: 0, y: 0 }
      scrollRef.current.last = { x: 0, y: 0 }

      container.innerHTML = ''

      const baseItems = data.map((d, i) => {
        const scaleX = tileSizeRef.current.w / originalSize.w
        const scaleY = tileSizeRef.current.h / originalSize.h
        const source = sources[i % sources.length]
        return {
          src: source.src,
          caption: source.caption,
          x: d.x * scaleX,
          y: d.y * scaleY,
          w: d.w * scaleX,
          h: d.h * scaleY,
        }
      })

      itemsRef.current = []
      const repsX = [0, tileSizeRef.current.w]
      const repsY = [0, tileSizeRef.current.h]

      baseItems.forEach((base) => {
        repsX.forEach((offsetX) => {
          repsY.forEach((offsetY) => {
            const el = document.createElement('div')
            el.classList.add('item')
            el.style.width = `${base.w}px`
            el.style.position = 'absolute'
            el.style.top = '0'
            el.style.left = '0'
            el.style.willChange = 'transform'
            el.style.whiteSpace = 'normal'

            const wrapper = document.createElement('div')
            wrapper.classList.add('item-wrapper')
            wrapper.style.willChange = 'transform'
            el.appendChild(wrapper)

            const itemImage = document.createElement('div')
            itemImage.classList.add('item-image')
            itemImage.style.width = `${base.w}px`
            itemImage.style.height = `${base.h}px`
            itemImage.style.overflow = 'hidden'
            wrapper.appendChild(itemImage)

            const img = new Image()
            img.src = base.src
            img.style.width = '100%'
            img.style.height = '100%'
            img.style.objectFit = 'cover'
            img.style.willChange = 'transform'
            itemImage.appendChild(img)

            const caption = document.createElement('small')
            caption.textContent = base.caption
            caption.style.width = '100%'
            caption.style.display = 'block'
            caption.style.fontSize = '0.75rem'
            caption.style.lineHeight = '1.25'
            caption.style.marginTop = '0.75rem'
            caption.style.fontFamily = FONT_STYLES.alga.fontFamily
            caption.style.letterSpacing = FONT_STYLES.alga.letterSpacing
            caption.style.color = 'white'
            wrapper.appendChild(caption)

            container.appendChild(el)

            itemsRef.current.push({
              el,
              wrapper,
              img,
              x: base.x + offsetX,
              y: base.y + offsetY,
              w: base.w,
              h: base.h,
              extraX: 0,
              extraY: 0,
              rect: el.getBoundingClientRect(),
              ease: Math.random() * 0.5 + 0.5,
            })
          })
        })
      })

      tileSizeRef.current.w *= 2
      tileSizeRef.current.h *= 2

      scrollRef.current.current.x = scrollRef.current.target.x = scrollRef.current.last.x = -winSizeRef.current.w * 0.1
      scrollRef.current.current.y = scrollRef.current.target.y = scrollRef.current.last.y = -winSizeRef.current.h * 0.1

      // Find intro items
      introItemsRef.current = Array.from(container.querySelectorAll('.item-wrapper')).filter((item) => {
        const rect = item.getBoundingClientRect()
        return (
          rect.x > -rect.width &&
          rect.x < window.innerWidth + rect.width &&
          rect.y > -rect.height &&
          rect.y < window.innerHeight + rect.height
        )
      }) as HTMLDivElement[]

      // Set initial positions for intro animation
      introItemsRef.current.forEach((item) => {
        const rect = item.getBoundingClientRect()
        const x = -rect.x + window.innerWidth * 0.5 - rect.width * 0.5
        const y = -rect.y + window.innerHeight * 0.5 - rect.height * 0.5
        gsap.set(item, { x, y })
      })

      // Intro animation
      gsap.to(introItemsRef.current.reverse(), {
        duration: GRID.INTRO_DURATION,
        ease: 'expo.inOut',
        x: 0,
        y: 0,
        stagger: GRID.INTRO_STAGGER,
      })
    }


    const onMouseDown = (e: MouseEvent) => {
      e.preventDefault()
      isDraggingRef.current = true
      document.documentElement.classList.add('dragging')
      mouseRef.current.press.t = 1
      dragRef.current.startX = e.clientX
      dragRef.current.startY = e.clientY
      dragRef.current.scrollX = scrollRef.current.target.x
      dragRef.current.scrollY = scrollRef.current.target.y
    }

    const onMouseUp = () => {
      isDraggingRef.current = false
      document.documentElement.classList.remove('dragging')
      mouseRef.current.press.t = 0
    }

    const onMouseMove = (e: MouseEvent) => {
      mouseRef.current.x.t = e.clientX / winSizeRef.current.w
      mouseRef.current.y.t = e.clientY / winSizeRef.current.h

      if (isDraggingRef.current) {
        const dx = e.clientX - dragRef.current.startX
        const dy = e.clientY - dragRef.current.startY
        scrollRef.current.target.x = dragRef.current.scrollX + dx
        scrollRef.current.target.y = dragRef.current.scrollY + dy
      }
    }

    const render = () => {
      if (!isAnimatingRef.current) return

      scrollRef.current.current.x +=
        (scrollRef.current.target.x - scrollRef.current.current.x) * scrollRef.current.ease
      scrollRef.current.current.y +=
        (scrollRef.current.target.y - scrollRef.current.current.y) * scrollRef.current.ease

      scrollRef.current.delta.x.t = scrollRef.current.current.x - scrollRef.current.last.x
      scrollRef.current.delta.y.t = scrollRef.current.current.y - scrollRef.current.last.y
      scrollRef.current.delta.x.c +=
        (scrollRef.current.delta.x.t - scrollRef.current.delta.x.c) * GRID.DELTA_EASE
      scrollRef.current.delta.y.c +=
        (scrollRef.current.delta.y.t - scrollRef.current.delta.y.c) * GRID.DELTA_EASE
      mouseRef.current.x.c += (mouseRef.current.x.t - mouseRef.current.x.c) * GRID.DELTA_EASE
      mouseRef.current.y.c += (mouseRef.current.y.t - mouseRef.current.y.c) * GRID.DELTA_EASE
      mouseRef.current.press.c += (mouseRef.current.press.t - mouseRef.current.press.c) * GRID.DELTA_EASE

      const dirX = scrollRef.current.current.x > scrollRef.current.last.x ? 'right' : 'left'
      const dirY = scrollRef.current.current.y > scrollRef.current.last.y ? 'down' : 'up'

      itemsRef.current.forEach((item) => {
        const newX =
          GRID.SCROLL_MULTIPLIER * scrollRef.current.delta.x.c * item.ease +
          (mouseRef.current.x.c - 0.5) * item.rect.width * GRID.MOUSE_PARALLAX
        const newY =
          GRID.SCROLL_MULTIPLIER * scrollRef.current.delta.y.c * item.ease +
          (mouseRef.current.y.c - 0.5) * item.rect.height * GRID.MOUSE_PARALLAX
        const scrollX = scrollRef.current.current.x
        const scrollY = scrollRef.current.current.y
        const posX = item.x + scrollX + item.extraX + newX
        const posY = item.y + scrollY + item.extraY + newY

        const beforeX = posX > winSizeRef.current.w
        const afterX = posX + item.rect.width < 0
        if (dirX === 'right' && beforeX) item.extraX -= tileSizeRef.current.w
        if (dirX === 'left' && afterX) item.extraX += tileSizeRef.current.w

        const beforeY = posY > winSizeRef.current.h
        const afterY = posY + item.rect.height < 0
        if (dirY === 'down' && beforeY) item.extraY -= tileSizeRef.current.h
        if (dirY === 'up' && afterY) item.extraY += tileSizeRef.current.h

        const fx = item.x + scrollX + item.extraX + newX
        const fy = item.y + scrollY + item.extraY + newY
        item.el.style.transform = `translate(${fx}px, ${fy}px)`
        const imgScale = GRID.IMG_SCALE_BASE + GRID.IMG_SCALE_PRESS * mouseRef.current.press.c * item.ease
        const imgTranslateX = -mouseRef.current.x.c * item.ease * GRID.IMG_TRANSLATE
        const imgTranslateY = -mouseRef.current.y.c * item.ease * GRID.IMG_TRANSLATE
        item.img.style.transform = `scale(${imgScale}) translate(${imgTranslateX}%, ${imgTranslateY}%)`
      })

      scrollRef.current.last.x = scrollRef.current.current.x
      scrollRef.current.last.y = scrollRef.current.current.y

      animationFrameRef.current = requestAnimationFrame(render)
    }

    const startAnimation = () => {
      if (!isAnimatingRef.current) {
        isAnimatingRef.current = true
        render()
      }
    }

    const stopAnimation = () => {
      isAnimatingRef.current = false
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
    }

    // Visibility-based animation pausing
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopAnimation()
      } else {
        startAnimation()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    window.addEventListener('resize', onResize)
    window.addEventListener('mousemove', onMouseMove)
    container.addEventListener('mousedown', onMouseDown)
    window.addEventListener('mouseup', onMouseUp)

    onResize()
    startAnimation()

    return () => {
      stopAnimation()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('resize', onResize)
      window.removeEventListener('mousemove', onMouseMove)
      container.removeEventListener('mousedown', onMouseDown)
      window.removeEventListener('mouseup', onMouseUp)
      document.documentElement.classList.remove('dragging')
    }
  }, [sources, data, originalSize])

  return (
    <section className="w-full h-screen overflow-hidden select-none cursor-grab relative flex-shrink-0 bg-black">
      <div
        ref={containerRef}
        className="w-full h-full inline-block whitespace-nowrap relative"
        style={{ width: '100%', height: '100vh' }}
      />
      <style>{`
        html.dragging section {
          cursor: grabbing;
        }
      `}</style>
    </section>
  )
}

